/**
 * @mention (people) suggestion configuration for the TipTap Mention extension.
 *
 * - Trigger: `@`
 * - API: GET /api/people/search?q=<query>
 * - Inserts an inline mention node with the person's id and name
 * - If no matching person exists, shows "Add person: <name>" option
 *   that POSTs to /api/people to create them first
 */

import { PluginKey } from '@tiptap/pm/state';
import type { SuggestionOptions } from '@tiptap/suggestion';
import { apiGet, apiPost } from '../../lib/api';
import { createSuggestionRenderer } from './suggestion-renderer';
import type { SuggestionItem } from './SuggestionList';

// API response shape from GET /api/people/search
interface PersonSearchResult {
  id: string;
  name: string;
  email?: string;
  role?: string;
}

export const MentionPluginKey = new PluginKey('mentionPeople');

export interface MentionSuggestionOptions {
  onMentionInserted?: (personId: string, personName: string) => void;
}

export function createMentionSuggestion(
  options?: MentionSuggestionOptions,
): Omit<SuggestionOptions<SuggestionItem>, 'editor'> {
  return {
    char: '@',
    pluginKey: MentionPluginKey,
    allowSpaces: true,

    items: async ({ query }) => {
      try {
        const results = await apiGet<PersonSearchResult[]>(
          `/people/search?q=${encodeURIComponent(query || '')}`,
        );
        const items: SuggestionItem[] = results.map(personToItem);

        // If the user typed something and there's no exact match, offer to create
        if (query && query.trim().length > 0) {
          const exactMatch = results.some(
            (r) => r.name.toLowerCase() === query.toLowerCase(),
          );
          if (!exactMatch) {
            items.push({
              id: `__create__${query}`,
              label: query.trim(),
              isCreate: true,
            });
          }
        }

        return items;
      } catch {
        // If API fails but user typed something, still offer to create
        if (query && query.trim().length > 0) {
          return [
            {
              id: `__create__${query}`,
              label: query.trim(),
              isCreate: true,
            },
          ];
        }
        return [];
      }
    },

    command: ({ editor, range, props: item }) => {
      if (item.isCreate) {
        // Create the person first, then insert the mention node
        apiPost<PersonSearchResult>('/people', { name: item.label })
          .then((created) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent({
                type: 'mention',
                attrs: {
                  id: created.id,
                  label: created.name,
                  mentionSuggestionChar: '@',
                },
              })
              .run();
            options?.onMentionInserted?.(created.id, created.name);
          })
          .catch((err) => {
            console.error('Failed to create person:', err);
            // Fall back to inserting with a placeholder id
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent({
                type: 'mention',
                attrs: {
                  id: null,
                  label: item.label,
                  mentionSuggestionChar: '@',
                },
              })
              .run();
          });
      } else {
        // Normal selection — insert mention node directly
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'mention',
            attrs: {
              id: item.id,
              label: item.label,
              mentionSuggestionChar: '@',
            },
          })
          .run();
        options?.onMentionInserted?.(item.id, item.label);
      }
    },

    render: createSuggestionRenderer({ variant: 'mention' }),
  };
}

/** @deprecated Use createMentionSuggestion() instead */
export const mentionSuggestion = createMentionSuggestion();

function personToItem(person: PersonSearchResult): SuggestionItem {
  return {
    id: person.id,
    label: person.name,
    secondary: person.email || person.role || undefined,
  };
}
